import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootReduxState } from "~/modules/app/components/ReduxProvider/types";
import {
    clearOpenNewChatRequest as clearOpenNewChatRequestAction,
    openNewChat as openNewChatAction,
    setChatOpen as setChatOpenAction,
} from "../store";
import { OpenNewChatPayload, OpenNewChatRequest } from "../store/types";

// // // //

/**
 * useAssetChat
 * React hook exposing the asset_chat Redux state and action dispatchers.
 *
 * Any component can call `openNewChat({ assetCorpusId, assetName })` to open a
 * fresh chat in the global dock, and read `isChatOpen` to tell whether a chat is
 * already up. Only GlobalChatDock should read `openNewChatRequest`, call
 * `clearOpenNewChatRequest`, or drive `setChatOpen`.
 */
export function useAssetChat(): {
    openNewChatRequest: OpenNewChatRequest | null;
    isChatOpen: boolean;
    openNewChat: (payload: OpenNewChatPayload) => void;
    clearOpenNewChatRequest: () => void;
    setChatOpen: (isOpen: boolean) => void;
} {
    const dispatch = useDispatch();
    const openNewChatRequest = useSelector(
        (state: RootReduxState) => state.assetChat.openNewChatRequest,
    );
    const isChatOpen = useSelector(
        (state: RootReduxState) => state.assetChat.isChatOpen,
    );

    // Stable dispatchers so callers can safely list them in effect deps.
    const openNewChat = useCallback(
        (payload: OpenNewChatPayload) => {
            dispatch(openNewChatAction(payload));
        },
        [dispatch],
    );
    const clearOpenNewChatRequest = useCallback(() => {
        dispatch(clearOpenNewChatRequestAction());
    }, [dispatch]);
    const setChatOpen = useCallback(
        (isOpen: boolean) => {
            dispatch(setChatOpenAction(isOpen));
        },
        [dispatch],
    );

    return {
        openNewChatRequest,
        isChatOpen,
        openNewChat,
        clearOpenNewChatRequest,
        setChatOpen,
    };
}
